import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip, Popconfirm } from 'antd';
import { autobind } from 'core-decorators';
import BaseComponent from 'components/BaseComponent';
import styledComp from 'components/StyledComp';
import DataTables from 'components-desktop/DataTables';
import TfootSummary from 'components-desktop/TfootSummary';
import helpers from 'misc/helpers';

const propTypes = {
  title: PropTypes.string,
  invoicePayments: PropTypes.array,
  onInvoicePaymentDelete: PropTypes.func.isRequired,
};

const defaultProps = {
  title: '',
  invoicePayments: [],
};

@autobind
class TablePaymentInvoices extends BaseComponent {
  constructor(props) {
    super(props);
  }

  async onDeleteClick(id) {
    const params = { id };
    await this.callService('/invoice_payment/delete', params);

    this.notifySuccess('Success', 'Invoice payment has been successfully deleted.');

    // Remove delete invoice payment from table.
    this.props.onInvoicePaymentDelete(id);
  }

  createColumns() {
    return [{
      title: 'Id',
      dataIndex: 'id',
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: 'ascend',
    }, {
      title: 'Invoice Number',
      dataIndex: 'invoiceNumber',
      sorter: (a, b) => a.invoiceNumber?.localeCompare(b.invoiceNumber),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/invoices/invoice_details?id=${record.invoiceId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Patient Name',
      dataIndex: 'patientName',
      sorter: (a, b) => a.patientName?.localeCompare(b.patientName),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/patients/patient_profile?id=${record.patientId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Authorization Code',
      dataIndex: 'authorizationCode',
      sorter: (a, b) => a.authorizationCode?.localeCompare(b.authorizationCode),
    }, {
      title: 'Invoice Amount',
      dataIndex: 'amountInDollars',
      sorter: (a, b) => a.amount - b.amount,
    }, {
      title: 'Received Amount',
      dataIndex: 'receivedInDollars',
      sorter: (a, b) => a.received - b.received,
      render: (text, record) => (
        <styledComp.Span
          width="wrap_content"
          height="wrap_content"
          color="textPrimaryDark"
          fontSize="12px"
          fontWeight="600"
        >
          {text}
        </styledComp.Span>
      ),
    }, {
      title: 'Remark',
      dataIndex: 'remark',
      sorter: (a, b) => a.remark?.localeCompare(b.remark),
      render: (text, record) => (
        <Tooltip title={text}>
          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            maxWidth="150px"
            color="textPrimaryDark"
            fontSize="12px"
            ellipsis
            display="inline-block"
          >
            {text}
          </styledComp.Span>
        </Tooltip>
      ),
    }, {
      title: 'Actions',
      render: (text, record) => (
        <Popconfirm
          title="Are you sure you want to delete this invoice payment?"
          placement="top"
          okText="Confirm"
          cancelText="Cancel"
          onConfirm={() => this.onDeleteClick(record.id)}
        >
          <styledComp.A
            width="wrap_content"
            height="wrap_content"
          >
            Delete
          </styledComp.A>
        </Popconfirm>
      ),
    }];
  }

  createDataSource(invoicePayments) {
    const dataSource = [];
    invoicePayments.forEach((invoicePayment, index) => {
      dataSource.push({
        key: invoicePayment.getId(),
        id: invoicePayment.getId(),
        invoiceId: invoicePayment.getLong('invoiceId'),
        invoiceNumber: invoicePayment.getRemoteObject('invoice').getString('invoiceNumber'),
        patientId: invoicePayment.getRemoteObject('invoice').getLong('patientId'),
        patientName: invoicePayment.getRemoteObject('invoice').getString('patientName'),
        authorizationCode: invoicePayment.getRemoteObject('invoice').getString('authorizationCode'),
        amount: invoicePayment.getRemoteObject('invoice').getDouble('amount'),
        amountInDollars: helpers.toDollars(invoicePayment.getRemoteObject('invoice').getDouble('amount')),
        received: invoicePayment.getDouble('received'),
        receivedInDollars: helpers.toDollars(invoicePayment.getDouble('received')),
        remark: invoicePayment.getString('remark'),
      });
    });
    return dataSource;
  }

  renderTfoot(columnsToDisplay, dataRowsToDisplay) {
    return (
      <TfootSummary
        columnsToDisplay={columnsToDisplay}
        dataRowsToDisplay={dataRowsToDisplay}
        isTableSelectable={false}
        columnKeysToAlign={['receivedInDollars']}
        reducers={[
          TfootSummary.SUM_REDUCER_GENERATOR('received')]
        }
        formatters={[
          n => helpers.toDollars(n),
        ]}
      />
    );
  }

  renderContent() {
    const { invoicePayments, title, onInvoicePaymentDelete, ...layoutProps } = this.props;

    return (
      <DataTables
        {...layoutProps}
        ref={(ref) => { this.dataTables = ref; }}
        color="yellow_gold"
        fontSize="12px"
        size="small"
        title={title}
        icon="money-collect"
        withColumnFilter={false}
        columns={this.createColumns()}
        dataSource={this.createDataSource(invoicePayments)}
        tfoot={this.renderTfoot}
      />
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
TablePaymentInvoices.propTypes = propTypes;
TablePaymentInvoices.defaultProps = defaultProps;

export default TablePaymentInvoices;
